import { supabase } from '@/lib/supabase';

export type TeacherListItem = {
  assignmentCount: number;
  created_at: string;
  email: string;
  full_name: string | null;
  id: string;
  is_active: boolean;
};

const CREATE_TEACHER_FUNCTION = 'admin-create-teacher';

export async function fetchTeachersForSchool(schoolId: string) {
  const [{ data: teachers, error: teachersError }, { data: assignments, error: assignmentsError }] =
    await Promise.all([
      supabase
        .from('profiles')
        .select('id, full_name, email, is_active, created_at')
        .eq('school_id', schoolId)
        .eq('role', 'teacher')
        .order('created_at', { ascending: false }),
      supabase.from('class_subjects').select('teacher_id').eq('school_id', schoolId),
    ]);

  if (teachersError) throw teachersError;
  if (assignmentsError) throw assignmentsError;

  const assignmentCounts = new Map<string, number>();

  for (const item of assignments ?? []) {
    assignmentCounts.set(item.teacher_id, (assignmentCounts.get(item.teacher_id) ?? 0) + 1);
  }

  return (teachers ?? []).map((teacher) => ({
    ...teacher,
    assignmentCount: assignmentCounts.get(teacher.id) ?? 0,
  })) as TeacherListItem[];
}

export async function createTeacherAccount(input: {
  email: string;
  fullName: string;
  password: string;
  schoolId: string;
}) {
  const { data, error } = await supabase.functions.invoke(CREATE_TEACHER_FUNCTION, {
    body: {
      email: input.email.trim(),
      fullName: input.fullName.trim(),
      password: input.password,
      role: 'teacher',
      schoolId: input.schoolId,
    },
  });

  if (error) throw error;

  return data;
}

export async function updateTeacherProfile(input: {
  fullName: string;
  schoolId: string;
  teacherId: string;
}) {
  const { error } = await supabase
    .from('profiles')
    .update({
      full_name: input.fullName.trim(),
    })
    .eq('id', input.teacherId)
    .eq('school_id', input.schoolId)
    .eq('role', 'teacher');

  if (error) throw error;
}

export async function toggleTeacherActive(input: {
  isActive: boolean;
  schoolId: string;
  teacherId: string;
}) {
  const { error } = await supabase
    .from('profiles')
    .update({
      is_active: input.isActive,
    })
    .eq('id', input.teacherId)
    .eq('school_id', input.schoolId)
    .eq('role', 'teacher');

  if (error) throw error;
}
